import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Brain } from 'lucide-react';

export const BRAIN_SEGMENTS = [
  { id: 'chat', label: 'Chat', href: '/admin/ai' },
  { id: 'knowledge', label: 'Knowledge base', href: '/admin/ai/knowledge' },
  { id: 'activity', label: 'Activity', href: '/admin/ai/activity' },
  { id: 'controls', label: 'Controls', href: '/admin/ai/controls' },
] as const;

export type BrainSegmentId = (typeof BRAIN_SEGMENTS)[number]['id'];

function segmentActive(pathname: string, href: string): boolean {
  if (href === '/admin/ai') {
    return pathname === '/admin/ai' || pathname === '/admin/ai/chat';
  }
  if (href === '/admin/ai/controls') {
    return (
      pathname.startsWith('/admin/ai/controls') ||
      pathname.startsWith('/admin/ai/management')
    );
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

type BrainShellProps = {
  title: string;
  description: string;
  actions?: ReactNode;
  children: ReactNode;
  /** Stretch to fill the admin content viewport (chat). */
  fullHeight?: boolean;
};

export function BrainShell({
  title,
  description,
  actions,
  children,
  fullHeight = false,
}: BrainShellProps) {
  const { pathname } = useLocation();

  return (
    <div
      className={
        fullHeight
          ? 'flex h-[calc(100vh-7rem)] min-h-[28rem] flex-col bg-gray-50'
          : 'min-h-full bg-gray-50'
      }
    >
      <div className="border-b border-gray-200 bg-white px-6 pt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
              <Brain className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
              <p className="text-sm text-gray-500">{description}</p>
            </div>
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </div>

        <nav className="mt-4 flex gap-1 overflow-x-auto" aria-label="Brain segments">
          {BRAIN_SEGMENTS.map((seg) => {
            const active = segmentActive(pathname, seg.href);
            return (
              <Link
                key={seg.id}
                to={seg.href}
                className={`-mb-px shrink-0 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {seg.label}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className={fullHeight ? 'flex min-h-0 flex-1 flex-col' : 'p-6'}>{children}</div>
    </div>
  );
}
